import Phaser from 'phaser';
import { gameConfig } from '@/config/gameConfig';
import { initSentry } from '@/sentry';
import { initDiscord } from '@/discord';
import { debugLog, isDebugEnabled } from '@/online/debug';
import { initProgressSync } from '@/services/progressSync';
import { initAnalytics } from '@/services/analytics';

// Initialize error tracking
initSentry();

// Initialize analytics (PostHog)
initAnalytics();

// Initialize Discord presence (silently fails if not in Discord context)
initDiscord();

// Sync local progress with Supabase (if configured)
// In Discord Activity mode, users are created on first sync via /api/user-scores
initProgressSync();

// Create and start the game
const game = new Phaser.Game(gameConfig);

const enableInputAfterFocus = (): void => {
  game.input.enabled = true;
  game.scene.scenes.forEach((scene) => {
    scene.input.enabled = true;
  });

  // Ensure the DOM overlay container doesn't eat pointer events after tab switches.
  if (game.domContainer) {
    game.domContainer.style.pointerEvents = 'none';
  }

  // Make canvas focusable for keyboard input.
  game.canvas.setAttribute('tabindex', '0');
  game.canvas.focus();

  debugLog('input_reenabled', {
    visibility: document.visibilityState,
    hasFocus: document.hasFocus?.(),
    inputEnabled: game.input.enabled,
  });
};

document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    debugLog('visibilitychange:hidden');
    return;
  }
  debugLog('visibilitychange:visible');
  enableInputAfterFocus();
});

window.addEventListener('focus', () => {
  debugLog('window:focus');
  enableInputAfterFocus();
});

window.addEventListener('blur', () => {
  debugLog('window:blur');
});

// When debugging tricky “UI stops responding” issues, this tells us if the browser is still delivering events
// and what element is receiving them (canvas vs DOM overlay).
if (isDebugEnabled()) {
  let lastPointerLogAt = 0;
  window.addEventListener('pointerdown', (event) => {
    const now = Date.now();
    if (now - lastPointerLogAt < 750) return;
    lastPointerLogAt = now;
    const target = event.target as HTMLElement | null;
    debugLog('pointerdown', {
      tag: target?.tagName,
      id: target?.id,
      className: target?.className,
    });
  }, true);
}

// Expose game instance for debugging in development
if (import.meta.env.DEV) {
  (window as unknown as { game: Phaser.Game }).game = game;
}
